import React, { useEffect, useState } from "react";

/**
 * Bottom-sheet shown when the user taps the "+" button next to the textarea.
 * Replaces a single file picker with three native pickers that match iMessage:
 *   - Foto      → accept="image/*,video/*" (opens photo library)
 *   - Cámara   → accept="image/*" capture="environment" (opens camera directly)
 *   - Documento → accept="application/pdf,.docx,.xlsx,.txt..." (opens file explorer)
 *
 * Props:
 *   onClose         — () => void
 *   onPickPhoto     — (files: FileList) => void
 *   onPickCamera    — (files: FileList) => void
 *   onPickDocument  — (files: FileList) => void
 */
export default function AttachSheet({ onClose, onPickPhoto, onPickCamera, onPickDocument }) {
  const [closing, setClosing] = useState(false);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const close = () => {
    setClosing(true);
    setTimeout(onClose, 180);
  };

  const handle = (cb) => (e) => {
    const files = e.target.files;
    if (files && files.length > 0) cb(files);
    // Reset so picking same file twice still triggers change
    e.target.value = "";
    close();
  };

  return (
    <div
      data-testid="attach-sheet"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        background: closing ? "rgba(0,0,0,0)" : "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
        zIndex: 65,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        transition: "background 180ms ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 480,
          background: "var(--pl-card)",
          border: "1px solid rgba(0, 200, 255, 0.22)",
          borderRadius: "20px 20px 0 0",
          padding: "8px 0 calc(8px + env(safe-area-inset-bottom, 0px))",
          boxShadow: "0 -12px 40px rgba(0,0,0,0.45)",
          transform: closing ? "translateY(100%)" : "translateY(0)",
          transition: "transform 180ms ease",
        }}
      >
        <div
          style={{
            width: 40,
            height: 4,
            background: "rgba(255,255,255,0.18)",
            borderRadius: 999,
            margin: "8px auto 14px",
          }}
        />

        {/* Each option = a label wrapping a hidden input so the tap opens the OS picker directly */}
        <AttachOption
          testId="attach-photo"
          label="Foto / Video"
          sub="Selecciona desde tu galería"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="var(--pl-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="3" />
              <circle cx="8.5" cy="8.5" r="1.5" fill="var(--pl-primary)" stroke="none" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          }
          inputProps={{
            accept: "image/*,video/*",
            multiple: true,
          }}
          onChange={handle(onPickPhoto)}
        />

        <AttachOption
          testId="attach-camera"
          label="Cámara"
          sub="Toma una foto al instante"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="var(--pl-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          }
          inputProps={{
            accept: "image/*",
            capture: "environment",
          }}
          onChange={handle(onPickCamera)}
        />

        <AttachOption
          testId="attach-doc"
          label="Documento"
          sub="PDF, Word, Excel, TXT…"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="var(--pl-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          }
          inputProps={{
            accept: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt",
            multiple: true,
          }}
          onChange={handle(onPickDocument)}
        />

        <button
          type="button"
          onClick={close}
          data-testid="attach-cancel"
          style={{
            display: "block",
            width: "calc(100% - 32px)",
            margin: "10px 16px 6px",
            padding: "12px 16px",
            borderRadius: 14,
            border: "1px solid var(--pl-border)",
            background: "transparent",
            color: "var(--pl-text)",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

function AttachOption({ testId, label, sub, icon, inputProps, onChange }) {
  return (
    <label
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        width: "100%",
        padding: "14px 20px",
        background: "transparent",
        border: "none",
        color: "var(--pl-text)",
        fontSize: 15,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      <span
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: "rgba(0, 200, 255, 0.08)",
          border: "1px solid rgba(0, 200, 255, 0.22)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "var(--pl-text-strong)", fontWeight: 700, fontSize: 15 }}>
          {label}
        </div>
        <div style={{ color: "var(--pl-text-muted)", fontSize: 12, marginTop: 2 }}>
          {sub}
        </div>
      </span>
      <input
        type="file"
        {...inputProps}
        onChange={onChange}
        style={{ display: "none" }}
      />
    </label>
  );
}
